import React, { useState, useEffect, useCallback } from "react";
import ReactDataGrid from "react-data-grid";
import Loader from "../../common/Loader";
import useWindowSize from "../../../hooks/useWindowSize";
import { hardware } from "../../../api/reports";
import { chunk, measureText } from "../../../helpers/utils";
import { Progress } from "reactstrap";
import moment from "moment";

const HardwareReportDisplay = ({ filterData, reportComplete }) => {
	const [loading, setLoading] = useState(true);
	const [data, setData] = useState({});
	const [columns, setColumns] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const size = useWindowSize();
	const [minHeight, setMinHeight] = useState(500);
	const [progress, setProgress] = useState(0);
	const [progressLength, setProgressLength] = useState(0);
	const [dateString, setDateString] = useState("");

	useEffect(() => {
		size.then((r) => setMinHeight(r.height - 225));

		var dashboardContentDiv = document.getElementById("dashboard-container-div");

		if (dashboardContentDiv) {
			var contentDiv = document.getElementById("hardware-report-div");

			if (contentDiv) {
				contentDiv.style.marginRight = `-${dashboardContentDiv.offsetLeft}px`;
			}
		}
	}, [size]);

	const updateProgress = useCallback(() => {
		setProgress((p) => p + 1);
	}, []);

	useEffect(() => {
		const LoadData = async () => {
			const { groupIds, period, startTime, endTime, showNullCount, showDateRange, showMinMaxAvg } = filterData;

			if (groupIds.length === 0) {
				setData(null);
				setRowCount(0);
				reportComplete(undefined);
			} else {
				let resultsData = null;

				const chunks = chunk(groupIds, 10);
				setProgress(0);
				setProgressLength(chunks.length);

				for (const i in chunks) {
					var result = await hardware(chunks[i].join(","), period, 1, startTime, endTime, showNullCount, showMinMaxAvg);
					updateProgress();

					if (showDateRange) {
						let temp = "";
						if (result.fromDateTime) {
							temp = `${moment(result.fromDateTime).format("DD/MM/YYYY HH:mm")} - `;
						}
						if (result.toDateTime) {
							temp = `${temp}${moment(result.toDateTime).format("DD/MM/YYYY HH:mm")}`;
						}
						setDateString(temp);
					}

					//First set?
					if (!resultsData) {
						resultsData = result;
					} else {
						//Append new rows
						resultsData.rows.push(...result.rows);
						resultsData.rowsCount = resultsData.rowsCount + result.rowsCount;

						//Append new columns
						for (const j in result.columns) {
							let found = false;
							for (const k in resultsData.columns) {
								if (result.columns[j].key === resultsData.columns[k].key) {
									found = true;
								}
							}
							if (!found) {
								resultsData.columns.push(result.columns[j]);
							}
						}
					}

					//Apply cell formatters
					resultsData.columns.forEach((column) => {
						switch (column.extra) {
							case "Colour":
								column.formatter = ValueFormatter;
								break;
							case "Battery":
								column.formatter = BatteryFormatter;
								break;
							case "LQI":
								column.formatter = LQIFormatter;
								break;
							default:
								column.formatter = NormalFormatter;
								break;
						}
					});

					//Reformat for no battery levels or signal
					resultsData.rows.forEach((row) => {
						if (row.BatteryLevel === 255) {
							row.BatteryLevel = "-";
						}
					});

					resultsData.rows.forEach((row) => {
						if (row.SignalStrength === 255) {
							row.SignalStrength = "-";
						}
					});

					//Sort data
					resultsData.rows.sort((a, b) => {
						if (a.BuildingIndex === b.BuildingIndex) {
							if (a.Building === b.Building) {
								if (a.ZoneIndex === b.ZoneIndex) {
									if (a.Zone === b.Zone) {
										if (a.GroupIndex === b.GroupIndex) {
											return a.Group > b.Group ? 1 : -1;
										} else {
											return a.GroupIndex > b.GroupIndex ? 1 : -1;
										}
									}
									return a.Zone > b.Zone ? 1 : -1;
								} else {
									return a.ZoneIndex > b.ZoneIndex ? 1 : -1;
								}
							} else {
								return a.Building > b.Building ? 1 : -1;
							}
						}
						return a.BuildingIndex > b.BuildingIndex ? 1 : -1;
					});

					//Resize columns 0,1,2,
					for (var col = 0; col < 3; ++col) {
						let width = (measureText(resultsData.columns[col].name, 13, null)?.width || 0) + 16;
						try {
							for (let i = 0; i < resultsData.rows.length; ++i) {
								const thisWidth = measureText(resultsData.rows[i][resultsData.columns[col].key], 12, null)?.width + 16;
								if (thisWidth > width) {
									width = thisWidth;
								}
							}
						} catch {}
						resultsData.columns[col].width = width;
					}

					//Show it
					setColumns(resultsData.columns.concat());
					setData(resultsData);
					setRowCount(resultsData.rowsCount); //This causes the redraw, if not done then only first set of data is shown
					setLoading(false);
				}
				reportComplete(resultsData);
			}
		};

		LoadData();
	}, [filterData, reportComplete, updateProgress]);

	const NormalFormatter = ({ value }) => (
		<div title={value} className="rdg_innerCell">
			{value}
		</div>
	);

	const ValueFormatter = ({ value }) => {
		return (
			value && (
				<div title={value} className="rdg_innerCell bg-secondary text-white">
					{value}
				</div>
			)
		);
	};

	const BatteryFormatter = ({ value }) => {
		if (value === "-") {
			return (
				<div title={value} className="rdg_innerCell bg-secondary text-white">
					{value}
				</div>
			);
		}
		if (value > -1 && value <= 10) {
			return (
				<div title={value} className="rdg_innerCell battery-very-low">
					{value}
				</div>
			);
		}
		if (value > 10 && value <= 20) {
			return (
				<div title={value} className="rdg_innerCell battery-low">
					{value}
				</div>
			);
		}
		return (
			value && (
				<div title={value} className="rdg_innerCell">
					{value}
				</div>
			)
		);
	};

	const LQIFormatter = ({ value }) => {
		if (value === "-") {
			return (
				<div title={value} className="rdg_innerCell bg-secondary text-white">
					{value}
				</div>
			);
		}
		return (
			value && (
				<div title={value} className="rdg_innerCell">
					{value}
				</div>
			)
		);
	};

	return (
		<>
			{loading ? (
				<Loader />
			) : (
				<>
					{filterData.showDateRange && <div>{dateString}</div>}
					<div className="rdg-no-gutters rounded-grid">{data && rowCount > 0 && <ReactDataGrid columns={columns} minColumnWidth={40} resizable={true} rowGetter={(i) => data.rows[i]} rowsCount={rowCount} minHeight={minHeight} enableRowSelect={null} />}</div>
				</>
			)}
			{progressLength > 0 && progress !== progressLength && <Progress animated value={progress} max={progressLength} />}
		</>
	);
};

export default HardwareReportDisplay;
